import { Col, Form, type FormInstance, Input, Modal, Row } from 'antd';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type { Produto } from '@/features/produto/types/Produto';
import type { ProdutoComposicaoBody } from '@/features/produto/types/ProdutoComposicao';
import type { Dialog } from '@/std/dialog/types';
import { InputNumber } from '@/components/input/InputNumber';
import { ProdutoSelect } from '@/features/produto/components/produto-select/ProdutoSelect';
import { numberToCurrency } from '@/std/format';
import { roundAbnt } from '@/std/math';
import { uuid } from '@/std/util';

export function ProdutoTabComposicaoKitModal({
    dialog,
    form,
    recalcularPercentuais,
    recalcularPrecoCusto,
    setFormDirty,
}: {
    dialog: Dialog<ProdutoComposicaoBody>;
    form: FormInstance<FormProdutoValues>;
    recalcularPercentuais: () => void;
    recalcularPrecoCusto: () => void;
    setFormDirty: (dirty: boolean) => void;
}) {
    const [formProdutoComposicao] = Form.useForm<ProdutoComposicaoBody>();

    const precoVenda = Form.useWatch('pro_preco_venda', form) ?? 0;
    const totalItemKit = Form.useWatch('totalItemKit', formProdutoComposicao) ?? 0;
    const novoPrecoVenda = precoVenda + totalItemKit;

    const pro_permite_venda_fracionada = Form.useWatch(
        ['produto', 'pro_permite_venda_fracionada'],
        formProdutoComposicao,
    );

    const permiteVendaFracionada = pro_permite_venda_fracionada === 1;

    const onChangeProduto = (idpk: number | null, produto: Produto | null): void => {
        const precoVenda = form.getFieldValue('pro_preco_venda') ?? 0;
        const precoVendaProduto = produto?.pro_preco_venda ?? 0;
        const newPrecoVenda = precoVenda + precoVendaProduto;
        const percentual =
            precoVenda > 0 ? roundAbnt((precoVendaProduto / newPrecoVenda) * 100, 4) : 100;

        formProdutoComposicao.setFieldsValue({
            prc_produto_filho_idpk: idpk,
            prc_quantidade: 1,
            prc_rateio_percentual: percentual,
            produto,
            totalItemKit: precoVendaProduto,
        });
    };

    const onChangeQuantidade = (quantidade: number | null): void => {
        const precoVenda = form.getFieldValue('pro_preco_venda') ?? 0;

        const precoVendaProduto =
            formProdutoComposicao.getFieldValue(['produto', 'pro_preco_venda']) ?? 0;

        const totalItemKit = precoVenda + precoVendaProduto * (quantidade ?? 0);

        const percentual =
            precoVenda > 0 ? roundAbnt((totalItemKit / (precoVenda + totalItemKit)) * 100, 4) : 100;

        formProdutoComposicao.setFieldsValue({
            prc_quantidade: quantidade,
            prc_rateio_percentual: percentual,
            totalItemKit,
        });
    };

    const onFinish = (item: ProdutoComposicaoBody): void => {
        // Not possible, just for type inference
        if (item.excluir === 'S') {
            return;
        }

        const precoVenda = form.getFieldValue('pro_preco_venda') ?? 0;
        const newPrecoVenda = precoVenda + (item.totalItemKit || 0);

        const produtoComposicao = form.getFieldValue('produto_composicao') || [];
        const produtoComposicaoAtivos = produtoComposicao.filter(
            (pc: ProdutoComposicaoBody) => pc.excluir !== 'S',
        );

        const newItem: ProdutoComposicaoBody = {
            ...item,
            key: uuid(),
            prc_ordem: produtoComposicaoAtivos.length + 1,
        };

        const newProdutoComposicao = [...produtoComposicao, newItem];

        form.setFieldsValue({
            pro_preco_venda: newPrecoVenda,
            produto_composicao: newProdutoComposicao,
        });

        setFormDirty(true);
        recalcularPercentuais();
        recalcularPrecoCusto();
        formProdutoComposicao.resetFields();
        dialog.actions.close();
    };

    return (
        <Modal
            afterOpenChange={(open) => {
                if (open) {
                    // Focus the ProdutoSelect after the modal animation completes
                    // autoFocus on BaseSelectSingle doesn't work because the select is rendered after the modal opens
                    setTimeout(() => {
                        const input = document.getElementById('produto-select');
                        input?.focus();
                    }, 100);
                }
            }}
            destroyOnHidden={true}
            onCancel={() => {
                formProdutoComposicao.resetFields();
                dialog.actions.close();
            }}
            onOk={() => formProdutoComposicao.submit()}
            open={dialog.state.open}
            title='Adicionar produto ao kit'
        >
            <Form form={formProdutoComposicao} layout='vertical' onFinish={onFinish}>
                <Form.Item hidden={true} name='produto' />

                <Row gutter={8}>
                    <Col span={24}>
                        <Form.Item
                            label='Produto'
                            name='prc_produto_filho_idpk'
                            rules={[{ message: 'Selecione um produto', required: true }]}
                        >
                            <ProdutoSelect id='produto-select' onChange={onChangeProduto} />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label='Unidade' name={['produto', 'unidade', 'pru_sigla']}>
                            <Input disabled={true} />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label='Custo' name={['produto', 'pro_preco_custo']}>
                            <InputNumber decimalScale={2} disabled={true} type='$' />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label='Venda' name={['produto', 'pro_preco_venda']}>
                            <InputNumber decimalScale={2} disabled={true} type='$' />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label='Quantidade' name='prc_quantidade'>
                            <InputNumber
                                decimalScale={permiteVendaFracionada ? 4 : 0}
                                onChange={onChangeQuantidade}
                            />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label='% do item no kit' name='prc_rateio_percentual'>
                            <InputNumber decimalScale={4} type='%' />
                        </Form.Item>
                    </Col>

                    <Col span={12}>
                        <Form.Item label='Total do item no kit' name='totalItemKit'>
                            <InputNumber decimalScale={4} type='$' />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            {totalItemKit > 0 && (
                <p className='text-center text-lg'>
                    Novo total do kit:{' '}
                    <span className='font-bold'>{numberToCurrency(novoPrecoVenda)}</span>
                </p>
            )}
        </Modal>
    );
}
